import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { Checkbox } from '@els/els-react--checkbox';
import { Pill } from '@els/els-react--pill';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import { Icon } from '@els/els-react--icon';
import { LinkElement } from '@els/els-react--link-element';
import { Loader } from '@els/els-react--loader';
import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import NavigationHistory from '@Services/NavigationHistory';
import HistoryHelper from '@Services/HistoryHelper';
import HistoryService from '@Services/History';
import Auth from '@Services/Auth';
import { dispatchCreateAlert } from '@Pages/Alerts/AlertsComponents/AlertCreateDialog/AlertCreateDialogActions';
import { isAlertVisible } from '@Pages/Alerts/AlertsUtils';

import { commaFormatNumber } from '@Utils/String';
import { resultsColors } from '@Constants/Results';
import { formatHistoryMetadataDate, formatHistorySavedDate, getHistorySelectionKey } from '../../utils';
import { historyReducerKey } from '../../Constants';
import { handleCheckboxChange, onSubmitForm, handleViewSaveModal, handleNameChange, handleSearchInput } from '../../HistoryAction';
import {
  trackHistoryViewSearchButtonClick,
  trackHistoryEditQueryButtonClick,
  trackHistoryCreateAlertButtonClick,
  trackHistorySaveQueryButtonClick,
} from '../../analytics';
import InlineRename from '../InlineRename';
import QueryItemContent from '../QueryItemContent';
import { getQueryDisplayState, getQueryText } from '../QueryItemContent/queryTextUtils';

import './HistoryListItem.scss';

function HistoryListItem({ item, type, time, recordCount, page, name, databaseName, groupItemIndex, itemIndex, historyTypes }) {
  const dispatch = useDispatch();
  // Default selectedItems to [] if undefined/null
  const { selectedItems: selectedItemsRaw } = useSelector((state) => state.get(historyReducerKey));
  const selectedItems = Array.isArray(selectedItemsRaw) ? selectedItemsRaw : [];
  const [editingItemId, setEditingItemId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isInputTouched, setIsInputTouched] = useState(false);
  // Re-run/view navigates to results after an async load. Show a blocking loader during that gap
  // so the click has immediate feedback; the loader clears when this row unmounts on navigation.
  const [isRerunning, setIsRerunning] = useState(false);
  // Mirrors QueryItemContent's collapse state; used only for row layout.
  const [isQueryExpanded, setIsQueryExpanded] = useState(false);

  const isEditing = editingItemId === item.resultName;

  const isTypeSupported = (historyType) => historyTypes && values(historyTypes)?.indexOf(historyType) !== -1;
  const hasFilters = Array.isArray(item?.filters) && item?.filters?.length > 0;
  const hasFilterComment = /filtered by/i.test(item?.comment || '');
  const isFilteredResultSet = item?.historyType === historyTypes?.MANUALLY_FILTERED || hasFilters || hasFilterComment;
  const isBatchSearch = item?.historyType === historyTypes?.BATCH_SEARCH;
  // QueryItemContent hides the structure thumbnail while a truncated query is collapsed,
  // so the checkbox offset must follow the same rule or it floats below an empty row.
  const shouldAlignCheckboxWithStructure = page === 'recent' && getQueryDisplayState(item, isQueryExpanded).showStructure;

  const setEditState = (nextEditingItemId = null, nextEditValue = '') => {
    setEditingItemId(nextEditingItemId);
    setEditValue(nextEditValue);
    setIsInputTouched(false);
  };

  const handleStartEdit = () => {
    setEditState(item.resultName, name || '');
  };

  const handleCancelEdit = () => {
    setEditState();
  };

  const handleSave = () => {
    const trimmedName = editValue.trim();
    if (trimmedName && trimmedName.length >= 3 && trimmedName.length <= 50) {
      dispatch(onSubmitForm(item, trimmedName, true));
      setTimeout(() => {
        handleCancelEdit();
      }, 500);
    }
  };

  // New: sync editValue to redux for HANDLE_NAME_CHANGE
  const handleEditValueChange = (value) => {
    setEditValue(value);
    setIsInputTouched(true);
    dispatch(handleNameChange(value));
  };

  const handleSaveSearch = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!isTypeSupported(item.historyType)) {
      return;
    }
    if (Auth.isAnonymousUser()) {
      HistoryService.openSaveHistoryModal({ ...item });
      return;
    }
    trackHistorySaveQueryButtonClick(page);
    dispatch(handleViewSaveModal(true, true, groupItemIndex, itemIndex, { ...item }));
  };

  const formattedResultCount = commaFormatNumber(recordCount);
  // Same precedence as the visible query text so the accessible name matches what is rendered.
  const checkboxDescription = getQueryText(item).trim();
  const checkboxLabel = checkboxDescription ? `Select ${checkboxDescription}` : 'Select Recent History Item';
  const metadataGroupLabel = 'Search Results';
  const actionGroupLabel = 'Query Actions';
  const primaryActionLabel = page === 'recent' ? 'Re-run search' : 'View search results';

  return (
    <li className="history-list-item__container u-els-display-flex">
      {isRerunning && <Loader isVisible isBlocking messages={{ LOADING_MESSAGE: 'Loading search results' }} />}
      <div
        className={cn('history-list-item__checkbox', { 'history-list-item__checkbox--with-structure': shouldAlignCheckboxWithStructure })}>
        <Checkbox
          data-e2e="select-history-item-checkbox"
          label={checkboxLabel}
          shouldHideLabel
          aria-label={checkboxLabel}
          checked={selectedItems.includes(getHistorySelectionKey(item))}
          onChange={() => {
            const selectionKey = getHistorySelectionKey(item);
            dispatch(handleCheckboxChange(selectionKey, !selectedItems.includes(selectionKey)));
          }}
        />
      </div>
      <div className={`history-list-item__content history-list-item__${page} u-els-margin-left-1o2`}>
        <div className="history-list-item__row history-list-item__row--primary">
          <div className="history-list-item__primary-text">
            {page === 'saved' ? (
              name && (
                <InlineRename
                  name={name}
                  isEditing={isEditing}
                  editValue={editValue}
                  isInputTouched={isInputTouched}
                  itemResultName={item.resultName}
                  onStartEdit={handleStartEdit}
                  onCancelEdit={handleCancelEdit}
                  onSave={handleSave}
                  onEditValueChange={handleEditValueChange}
                  onBlur={() => setIsInputTouched(true)}
                  item={item}
                />
              )
            ) : (
              <QueryItemContent historyItem={cloneDeep(item)} page={page} onExpandedChange={setIsQueryExpanded} />
            )}
          </div>
        </div>
        {page === 'saved' && (
          <div className="history-list-item__saved-query">
            <QueryItemContent historyItem={cloneDeep(item)} page={page} onExpandedChange={setIsQueryExpanded} />
          </div>
        )}
        <div className="history-list-item__row history-list-item__row--meta">
          <div
            className="history-list-item__metadata rx-align-center u-els-align-items-center"
            role="group"
            aria-label={metadataGroupLabel}>
            <div className="u-els-font-size-base u-els-font-family-bold">{formattedResultCount}</div>
            <div>
              <Pill pillColor={resultsColors[type]} textColor="text">
                {type}
              </Pill>
            </div>
            {page === 'recent' && databaseName && <div className=" u-els-font-family-bold">in {databaseName}</div>}
            <div>{page === 'recent' ? formatHistoryMetadataDate(time) : formatHistorySavedDate(time)}</div>
          </div>
          <div className="history-list-item__actions" role="group" aria-label={actionGroupLabel}>
            {page === 'recent' && (
              <div className="history-list-item__recent-actions">
                {(isFilteredResultSet || isBatchSearch) && (
                  <Flyout
                    trigger="click"
                    placement="top-start"
                    className="history-list-item__filter-flyout"
                    shouldCloseOnESC
                    showCloseButton
                    width="19rem"
                    flyout={
                      <span className="history-list-item__filtered-warning-flyout">
                        {isBatchSearch ? 'This batch search is temporary.' : 'This refined search is temporary.'}
                        {` `}
                        <LinkElement data-e2e="e2e-history-recent-flyout-save-link" href="#" inline navigation onClick={handleSaveSearch}>
                          Save
                        </LinkElement>
                        {` `}
                        it to keep it.
                      </span>
                    }>
                    <Button
                      data-e2e="e2e-history-recent-filter-warning"
                      type="transparent"
                      aria-label={
                        isBatchSearch
                          ? 'This batch search is temporary. Save it to keep it.'
                          : 'This refined search is temporary. Save it to keep it.'
                      }
                      className="c-els-button--icon-only u-els-margin-right-1ox rx-cursor-pointer"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}>
                      <Icon sprite="Alert" color="alert" size="xs" isDecorative />
                    </Button>
                  </Flyout>
                )}
                <ButtonWithIcon
                  data-e2e="e2e-history-recent-save"
                  name="save"
                  type="link"
                  sprite="SaveFile"
                  linkIconSize="xs"
                  iconRight
                  disabled={!isTypeSupported(item.historyType)}
                  onClick={handleSaveSearch}
                  className="e2e-history-recent-save u-els-margin-right">
                  Save search
                </ButtonWithIcon>
              </div>
            )}
            <ButtonWithIcon
              data-e2e={page === 'saved' ? 'e2e-history-saved-open-alert' : 'e2e-history-recent-open-alert'}
              iconRight
              size="small"
              sprite="Alarm"
              linkIconSize="xs"
              type="link"
              disabled={!isAlertVisible(item)}
              title={!isAlertVisible(item) ? 'Creating Alerts is not available for this search' : undefined}
              aria-label={!isAlertVisible(item) ? 'Create alert (not available for this search)' : 'Create alert'}
              onClick={() => {
                trackHistoryCreateAlertButtonClick(page);
                dispatchCreateAlert(item);
              }}
              className="u-els-margin-right">
              Create Alert
            </ButtonWithIcon>
            <ButtonWithIcon
              name="edit-query"
              type="link"
              sprite="Writing"
              linkIconSize="xs"
              iconRight
              disabled={item.historyType !== historyTypes.QUERY_BUILDER && !item.quickSearchItem?.whereClauseImportToken}
              onClick={() => {
                trackHistoryEditQueryButtonClick(page);
                HistoryHelper.copyQuery(item);
              }}
              data-e2e="e2e-history-saved-edit-query">
              Edit in Query Builder
            </ButtonWithIcon>
          </div>
        </div>
        <div className="history-list-item__primary-action">
          <Button
            data-e2e="view-search-button"
            size="small"
            type="secondary"
            disabled={item.resultSize === 0 || isRerunning}
            className="history-list-item__primary-action-button"
            onClick={() => {
              setIsRerunning(true);
              dispatch(handleSearchInput(''));
              trackHistoryViewSearchButtonClick(page);
              const navigationPromise = NavigationHistory.viewHistory(item);
              if (navigationPromise?.catch) {
                navigationPromise.catch(() => setIsRerunning(false));
              }
            }}
            aria-label={primaryActionLabel}>
            {page === 'recent' ? 'Re-run search' : 'View search'}
          </Button>
        </div>
      </div>
      <hr className="u-els-margin-top" />
    </li>
  );
}

HistoryListItem.propTypes = {
  item: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  time: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  recordCount: PropTypes.number,
  page: PropTypes.string.isRequired,
  name: PropTypes.string,
  databaseName: PropTypes.string,
  groupItemIndex: PropTypes.number.isRequired,
  itemIndex: PropTypes.number.isRequired,
  historyTypes: PropTypes.object,
};

export default HistoryListItem;
